import { z } from 'zod';

import { DomainError } from './errors.ts';

/**
 * The media types a study file may be stored as. The bytes decide which one a file is, never its
 * name or the type a browser claimed for it: PDF, UTF-8 plain text, and three image formats.
 */
export const STUDY_FILE_MEDIA_TYPES = [
  'application/pdf',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;
export type StudyFileMediaType = (typeof STUDY_FILE_MEDIA_TYPES)[number];

/** The largest file a study keeps, in bytes: 10 MiB. */
export const MAX_MATERIALS_FILE_BYTES = 10_485_760;

/** The longest file name a study file keeps, in characters. */
export const MAX_STUDY_FILE_NAME_CHARS = 255;

/**
 * The most bytes one study keeps across every file it was ever given, 100 MiB. Superseded files
 * and files no version names count too: a stored file is kept until the draft study is deleted.
 */
export const MAX_STUDY_FILE_BYTES = 104_857_600;

/** The most files one text intake names in a version. A paired version names at most one. */
export const MAX_TEXT_INTAKE_FILES = 5;

/** The most pages of one PDF a reading reads; later pages are reported unread. */
export const MAX_PDF_PAGES_READ = 50;

/** The most characters of file text one reading reads, across all its files in input order. */
export const MAX_READING_FILE_CHARS = 60_000;

/** The pixel bounds on each side of an image, inclusive. */
export const MIN_IMAGE_SIDE_PX = 100;
export const MAX_IMAGE_SIDE_PX = 8000;

/**
 * What a stored file is to the Materials: an image, which the model reads as an image and which a
 * researcher may offer to participants, or a document, which is read as text.
 */
export const STUDY_FILE_KINDS = ['document', 'image'] as const;
export type StudyFileKind = (typeof STUDY_FILE_KINDS)[number];

export const studyFileKindOf = (mediaType: StudyFileMediaType): StudyFileKind =>
  mediaType.startsWith('image/') ? 'image' : 'document';

/** What the bytes of an accepted file were found to be. */
export interface SniffedStudyFile {
  mediaType: StudyFileMediaType;
  kind: StudyFileKind;
  width: number | null;
  height: number | null;
}

/** Why an upload is refused, each code with the detail its refusal carries. */
export const STUDY_FILE_REFUSAL_CODES = [
  'file_name_invalid',
  'file_too_large',
  'file_type_unsupported',
  'file_damaged',
  'image_dimensions_out_of_range',
] as const;
export type StudyFileRefusalCode = (typeof STUDY_FILE_REFUSAL_CODES)[number];

export type StudyFileRefusal =
  | { code: 'file_name_invalid' }
  | { code: 'file_too_large' }
  | { code: 'file_type_unsupported'; reason: 'office_document' | 'unsupported' }
  | { code: 'file_damaged' }
  | { code: 'image_dimensions_out_of_range'; width: number; height: number };

export type StudyFileSniff =
  { ok: true; file: SniffedStudyFile } | { ok: false; refusal: StudyFileRefusal };

/** The bytes in one mebibyte, the unit every file size a researcher is shown is stated in. */
export const BYTES_PER_MEBIBYTE = 1_048_576;

/** A byte count in whole mebibytes, as the limits a researcher is told about are stated. */
export const mebibytes = (bytes: number): string => `${String(bytes / BYTES_PER_MEBIBYTE)} MiB`;

/** What the researcher is told for each refusal, each limit taken from the constant it states. */
const REFUSAL_MESSAGES: Record<StudyFileRefusalCode, string> = {
  file_name_invalid: `Rename the file: its name needs to be 1 to ${String(MAX_STUDY_FILE_NAME_CHARS)} characters with no slashes or control characters.`,
  file_too_large: `Files can be up to ${mebibytes(MAX_MATERIALS_FILE_BYTES)}.`,
  file_type_unsupported: 'Sigora reads PDF, text (.txt, .md), PNG, JPEG and WebP files.',
  file_damaged: 'This file looks damaged or incomplete. Try uploading it again.',
  image_dimensions_out_of_range: `Images need to be between ${MIN_IMAGE_SIDE_PX.toLocaleString('en-US')} and ${MAX_IMAGE_SIDE_PX.toLocaleString('en-US')} pixels on each side.`,
};

/** The refusal as the error a route answers with. */
export const studyFileRefusalError = (refusal: StudyFileRefusal): DomainError => {
  if (refusal.code === 'file_type_unsupported' && refusal.reason === 'office_document') {
    return new DomainError('file_type_unsupported', 'Save it as a PDF and upload the PDF.', {
      reason: refusal.reason,
    });
  }

  const { code, ...detail } = refusal;

  return new DomainError(code, REFUSAL_MESSAGES[code], detail);
};

/**
 * Whether a name may be kept as a file's name: 1 to 255 characters, not blank, and with no path
 * separator or control character, so it can be shown and sent back in a header as it is.
 */
// oxlint-disable-next-line no-control-regex -- control characters are exactly what this refuses.
const UNSAFE_NAME_CHARACTER = /[/\\\u0000-\u001f\u007f-\u009f]/u;

export const isStudyFileName = (name: string): boolean => {
  const characters = [...name].length;

  return (
    characters >= 1 &&
    characters <= MAX_STUDY_FILE_NAME_CHARS &&
    /\S/u.test(name) &&
    !UNSAFE_NAME_CHARACTER.test(name)
  );
};

/**
 * A stored file as a screen or a route sees it: everything but the bytes and their hash. `width`
 * and `height` are set for an image and for nothing else.
 */
export const studyFileMetaSchema = z.object({
  id: z.uuid(),
  name: z.string().refine(isStudyFileName, { message: 'not a file name Sigora keeps' }),
  mediaType: z.enum(STUDY_FILE_MEDIA_TYPES),
  kind: z.enum(STUDY_FILE_KINDS),
  sizeBytes: z.int().positive(),
  width: z.int().positive().nullable(),
  height: z.int().positive().nullable(),
  createdAt: z.iso.datetime(),
});
export type StudyFileMeta = z.infer<typeof studyFileMetaSchema>;

/** The name the researcher gave each of `files`, by file id. */
export const fileNamesById = (files: Iterable<{ id: string; name: string }>): Map<string, string> =>
  new Map([...files].map((file) => [file.id, file.name]));

const startsWith = (bytes: Uint8Array, prefix: readonly number[], offset = 0): boolean =>
  bytes.length >= offset + prefix.length &&
  prefix.every((byte, index) => bytes[offset + index] === byte);

const ascii = (text: string): number[] => [...text].map((character) => character.charCodeAt(0));

const PDF_MAGIC = ascii('%PDF-');
const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_MAGIC = [0xff, 0xd8, 0xff];
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];

const u16be = (bytes: Uint8Array, at: number): number =>
  ((bytes[at] ?? 0) << 8) | (bytes[at + 1] ?? 0);
const u16le = (bytes: Uint8Array, at: number): number =>
  (bytes[at] ?? 0) | ((bytes[at + 1] ?? 0) << 8);
const u24le = (bytes: Uint8Array, at: number): number =>
  u16le(bytes, at) | ((bytes[at + 2] ?? 0) << 16);
const u32be = (bytes: Uint8Array, at: number): number =>
  (bytes[at] ?? 0) * 0x1000000 + u16be(bytes, at + 1) * 0x100 + (bytes[at + 3] ?? 0);
const u32le = (bytes: Uint8Array, at: number): number =>
  u24le(bytes, at) + (bytes[at + 3] ?? 0) * 0x1000000;

const isWebp = (bytes: Uint8Array): boolean =>
  startsWith(bytes, ascii('RIFF')) && startsWith(bytes, ascii('WEBP'), 8);

/** The image type the magic bytes name, or `null` for anything else. */
const imageTypeOf = (bytes: Uint8Array): StudyFileMediaType | null => {
  if (startsWith(bytes, PNG_MAGIC)) {
    return 'image/png';
  }

  if (startsWith(bytes, JPEG_MAGIC)) {
    return 'image/jpeg';
  }

  return isWebp(bytes) ? 'image/webp' : null;
};

/** A PNG's size from its IHDR, which must be the first chunk, exactly 13 bytes, and whole. */
const pngDimensions = (bytes: Uint8Array): { width: number; height: number } | null =>
  bytes.length >= 8 + 8 + 13 + 4 && u32be(bytes, 8) === 13 && startsWith(bytes, ascii('IHDR'), 12)
    ? { width: u32be(bytes, 16), height: u32be(bytes, 20) }
    : null;

/**
 * A JPEG's size from its first start-of-frame marker, walking the marker segments from the start of
 * the file. SOF0 to SOF15 carry it, except the three codes in that range that name other segments.
 * Every segment must fit the file, and a frame's declared length must be the one its component
 * count gives (8 bytes, then 3 per component), so the size is never read from the bytes after it.
 */
const jpegDimensions = (bytes: Uint8Array): { width: number; height: number } | null => {
  let at = 2;
  while (at + 4 <= bytes.length) {
    if (bytes[at] !== 0xff) {
      return null;
    }

    const marker = bytes[at + 1] ?? 0;
    if (marker === 0xff) {
      at += 1;
      continue;
    }

    // Markers that stand alone, with no length after them.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      at += 2;
      continue;
    }

    if (marker === 0xd9 || marker === 0xda) {
      return null;
    }

    const length = u16be(bytes, at + 2);
    if (length < 2 || at + 2 + length > bytes.length) {
      return null;
    }

    const isFrame = marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker);
    if (isFrame) {
      const components = bytes[at + 9] ?? 0;
      return length >= 8 && length === 8 + 3 * components
        ? { width: u16be(bytes, at + 7), height: u16be(bytes, at + 5) }
        : null;
    }

    at += 2 + length;
  }

  return null;
};

/**
 * A WebP's canvas size from its first chunk: lossy (`VP8 `), lossless (`VP8L`) or extended (`VP8X`).
 * The chunk's declared size must hold the fields read, and the file must hold the chunk that far.
 */
const webpDimensions = (bytes: Uint8Array): { width: number; height: number } | null => {
  const firstType = String.fromCharCode(...bytes.subarray(12, 16));
  const needed = firstType === 'VP8L' ? 5 : 10;
  if (u32le(bytes, 16) < needed || bytes.length < 20 + needed) {
    return null;
  }

  if (startsWith(bytes, ascii('VP8 '), 12)) {
    return bytes.length >= 30 && startsWith(bytes, [0x9d, 0x01, 0x2a], 23)
      ? { width: u16le(bytes, 26) & 0x3fff, height: u16le(bytes, 28) & 0x3fff }
      : null;
  }

  if (startsWith(bytes, ascii('VP8L'), 12)) {
    if (bytes.length < 25 || bytes[20] !== 0x2f) {
      return null;
    }

    const bits = u32le(bytes, 21);

    return { width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
  }

  if (startsWith(bytes, ascii('VP8X'), 12)) {
    return bytes.length >= 30
      ? { width: u24le(bytes, 24) + 1, height: u24le(bytes, 27) + 1 }
      : null;
  }

  return null;
};

/**
 * An image's pixel size as its header states it, or `null` when the header cannot be read. Only the
 * header is read: nothing here decodes pixels.
 */
export const imageDimensions = (
  bytes: Uint8Array,
  mediaType: StudyFileMediaType,
): { width: number; height: number } | null => {
  if (mediaType === 'image/png') {
    return startsWith(bytes, PNG_MAGIC) ? pngDimensions(bytes) : null;
  }

  if (mediaType === 'image/jpeg') {
    return startsWith(bytes, JPEG_MAGIC) ? jpegDimensions(bytes) : null;
  }

  if (mediaType === 'image/webp') {
    return isWebp(bytes) ? webpDimensions(bytes) : null;
  }

  return null;
};

/**
 * Whether a PNG's chunks run from its signature to an IEND chunk, each one's length within the
 * file. Bytes after IEND are ignored: some encoders and apps pad or append to a PNG.
 */
const pngComplete = (bytes: Uint8Array): boolean => {
  let at = PNG_MAGIC.length;
  while (at + 12 <= bytes.length) {
    const length = u32be(bytes, at);
    const next = at + 12 + length;
    if (next > bytes.length) {
      return false;
    }

    if (startsWith(bytes, ascii('IEND'), at + 4)) {
      return length === 0;
    }

    at = next;
  }

  return false;
};

/**
 * Whether a JPEG's segments reach the start of its scan and an end-of-image marker follows it.
 * Inside the scan a 0xFF data byte is always followed by 0x00 or a restart marker, so the first
 * FF D9 after the scan starts is the end of the image. Bytes after it are ignored: phones append
 * a motion video or other trailers there.
 */
const jpegComplete = (bytes: Uint8Array): boolean => {
  let at = 2;
  while (at + 4 <= bytes.length) {
    if (bytes[at] !== 0xff) {
      return false;
    }

    const marker = bytes[at + 1] ?? 0;
    if (marker === 0xff) {
      at += 1;
      continue;
    }

    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      at += 2;
      continue;
    }

    if (marker === 0xd9) {
      return false;
    }

    const length = u16be(bytes, at + 2);
    if (length < 2 || at + 2 + length > bytes.length) {
      return false;
    }

    if (marker === 0xda) {
      for (let scan = at + 2 + length; scan + 1 < bytes.length; scan += 1) {
        if (bytes[scan] === 0xff && bytes[scan + 1] === 0xd9) {
          return true;
        }
      }

      return false;
    }

    at += 2 + length;
  }

  return false;
};

/**
 * A WebP's chunks in order, each one and its pad byte inside the RIFF, and the RIFF inside the file;
 * `null` when any of them runs past its end. Bytes after the RIFF are ignored.
 */
const webpChunks = (bytes: Uint8Array): { type: string; size: number }[] | null => {
  const riffEnd = 8 + u32le(bytes, 4);
  if (bytes.length < 12 || riffEnd > bytes.length) {
    return null;
  }

  const chunks: { type: string; size: number }[] = [];
  let at = 12;
  while (at < riffEnd) {
    if (at + 8 > riffEnd) {
      return null;
    }

    const size = u32le(bytes, at + 4);
    const end = at + 8 + size + (size % 2);
    if (end > riffEnd) {
      return null;
    }

    chunks.push({ type: String.fromCharCode(...bytes.subarray(at, at + 4)), size });
    at = end;
  }

  return chunks;
};

/** The smallest lossy and lossless bitstreams whose headers carry a frame's size. */
const WEBP_PAYLOAD_MIN: Readonly<Record<string, number>> = { 'VP8 ': 10, VP8L: 5 };

/**
 * Whether a still WebP's chunks are whole and hold its image: a lossy or lossless bitstream as the
 * first chunk, or after an extended header.
 */
const webpComplete = (bytes: Uint8Array): boolean => {
  const chunks = webpChunks(bytes);
  if (chunks === null || chunks.length === 0) {
    return false;
  }

  const payloads = chunks[0]?.type === 'VP8X' ? chunks.slice(1) : chunks.slice(0, 1);

  return payloads.some(
    (chunk) =>
      WEBP_PAYLOAD_MIN[chunk.type] !== undefined &&
      chunk.size >= (WEBP_PAYLOAD_MIN[chunk.type] ?? 0),
  );
};

/** Whether a WebP is animated: its extended header says so, or it holds animation frames. */
const isAnimatedWebp = (bytes: Uint8Array): boolean =>
  (startsWith(bytes, ascii('VP8X'), 12) && ((bytes[20] ?? 0) & 0x02) !== 0) ||
  (webpChunks(bytes) ?? []).some((chunk) => chunk.type === 'ANIM' || chunk.type === 'ANMF');

/**
 * Whether an image's structure is whole: a PNG's chunks reach IEND, a JPEG's scan is followed by its
 * end-of-image marker, a WebP's chunks fit its RIFF and hold an image bitstream. A truncated or interrupted
 * upload fails this, which is what keeps it from reaching a reading as a file the model cannot open;
 * bytes an image carries after its end do not.
 */
export const imageIsComplete = (bytes: Uint8Array, mediaType: StudyFileMediaType): boolean => {
  if (mediaType === 'image/png') {
    return startsWith(bytes, PNG_MAGIC) && pngComplete(bytes);
  }

  if (mediaType === 'image/jpeg') {
    return startsWith(bytes, JPEG_MAGIC) && jpegComplete(bytes);
  }

  if (mediaType === 'image/webp') {
    return isWebp(bytes) && webpComplete(bytes);
  }

  return false;
};

const OFFICE_ENTRY_PREFIXES = ['word/', 'ppt/', 'xl/'];
const decodeName = new TextDecoder('utf-8');

/**
 * Whether a zip's central directory names an Office document's parts. Read from the end of the
 * archive, where the directory sits with every entry's name uncompressed; an archive whose directory
 * cannot be found is not one.
 */
const isOfficeDocument = (bytes: Uint8Array): boolean => {
  const end = [0x50, 0x4b, 0x05, 0x06];
  // The end record is 22 bytes plus a comment of at most 65,535.
  const earliest = Math.max(0, bytes.length - 22 - 0xffff);
  let record = -1;
  for (let at = bytes.length - 22; at >= earliest; at -= 1) {
    if (startsWith(bytes, end, at)) {
      record = at;
      break;
    }
  }

  if (record < 0) {
    return false;
  }

  const entries = u16le(bytes, record + 10);
  let at = u32le(bytes, record + 16);
  for (let entry = 0; entry < entries; entry += 1) {
    if (!startsWith(bytes, [0x50, 0x4b, 0x01, 0x02], at)) {
      return false;
    }

    const nameLength = u16le(bytes, at + 28);
    const extraLength = u16le(bytes, at + 30);
    const commentLength = u16le(bytes, at + 32);
    if (at + 46 + nameLength > bytes.length) {
      return false;
    }

    const name = decodeName.decode(bytes.subarray(at + 46, at + 46 + nameLength));
    if (OFFICE_ENTRY_PREFIXES.some((prefix) => name.startsWith(prefix))) {
      return true;
    }

    at += 46 + nameLength + extraLength + commentLength;
  }

  return false;
};

const TEXT_EXTENSION = /\.(?:txt|md)$/iu;

/** Whether bytes are UTF-8 text with no NUL byte: what a `.txt` or `.md` file must be. */
const isPlainText = (bytes: Uint8Array): boolean => {
  if (bytes.includes(0)) {
    return false;
  }

  try {
    new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return true;
  } catch {
    return false;
  }
};

const refused = (refusal: StudyFileRefusal): StudyFileSniff => ({ ok: false, refusal });

/**
 * What an upload is, decided from its bytes, or why it is refused.
 *
 * Magic bytes decide a binary type, so a PNG named `.pdf` is accepted as the PNG it is and a renamed
 * executable, which has no accepted magic, is refused. Text is the one type with no magic: it is
 * accepted only under a `.txt` or `.md` name and only when the bytes are UTF-8 with no NUL byte. An
 * image must state dimensions within bounds and end intact, and a WebP must be a still image. Whether
 * a PDF opens is left to the
 * reading, which is the only place a PDF is parsed.
 */
export const sniffStudyFile = (bytes: Uint8Array, name: string): StudyFileSniff => {
  if (!isStudyFileName(name)) {
    return refused({ code: 'file_name_invalid' });
  }

  if (bytes.length > MAX_MATERIALS_FILE_BYTES) {
    return refused({ code: 'file_too_large' });
  }

  if (bytes.length === 0) {
    return refused({ code: 'file_damaged' });
  }

  if (startsWith(bytes, PDF_MAGIC)) {
    return {
      ok: true,
      file: { mediaType: 'application/pdf', kind: 'document', width: null, height: null },
    };
  }

  const imageType = imageTypeOf(bytes);
  if (imageType !== null) {
    // An animated WebP is not an image a model reads or a participant is shown as a still.
    if (imageType === 'image/webp' && isAnimatedWebp(bytes)) {
      return refused({ code: 'file_type_unsupported', reason: 'unsupported' });
    }

    const dimensions = imageDimensions(bytes, imageType);
    if (dimensions === null || !imageIsComplete(bytes, imageType)) {
      return refused({ code: 'file_damaged' });
    }

    const { width, height } = dimensions;
    const within = (side: number) => side >= MIN_IMAGE_SIDE_PX && side <= MAX_IMAGE_SIDE_PX;
    if (!within(width) || !within(height)) {
      return refused({ code: 'image_dimensions_out_of_range', width, height });
    }

    return { ok: true, file: { mediaType: imageType, kind: 'image', width, height } };
  }

  if (startsWith(bytes, ZIP_MAGIC)) {
    return refused({
      code: 'file_type_unsupported',
      reason: isOfficeDocument(bytes) ? 'office_document' : 'unsupported',
    });
  }

  if (TEXT_EXTENSION.test(name) && isPlainText(bytes)) {
    return {
      ok: true,
      file: { mediaType: 'text/plain', kind: 'document', width: null, height: null },
    };
  }

  return refused({ code: 'file_type_unsupported', reason: 'unsupported' });
};
